// Angular
import { BehaviorSubject, Observable, Subject, filter, map, mergeMap, of, take, takeUntil, tap } from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

// Libraries

import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

// Models
import { ApiResponse, AppCleanup, LineStyleOption, LineStyles } from '../../../shared/models';
import { GenerateJobLinkPayload, GetDataResponseCode, GetJobDataPayload, Job, JobData, JobDataResponse, JobLink, JobOptions, JobStatus, SplitMergeJobState, SplitMergeResult } from '../models';
import { JobBaseDto } from '../models/job-base-dto.model';
import { JobCommentReturnDto } from '../models/job-comment-return-dto';
import { JobCommentDto } from '../models/job-comment-dto';
import { JobCommentDefaultGroupLocalStorage } from '../../comment/models/job-comment-default-group-dto';
import { UnitPeriodChannelDto } from '../../channel/models/unit-period-channel-dto.model';
import { UserUnitPeriodChannelDto } from '../../channel/models/user-unit-period-channel-dto';
import { UnitPeriodChannelOffsetDto } from '../models/unit-period-channel-offset-dto.model';
import { UnitPeriodDto } from '../models/unit-period-dto.model';
import { JobChange, JobUpdate } from '../models/job.model';
import { SplitMergeJobPayload } from '../models/split-merge-job-payload.model';
import { UnitPeriodJobChangeDto } from '../models/unit-period-job-change-dto';
import { JobTypeChannelMappingDto } from '../models/job-type-channel-mapping-dto.model';
import { JobAttachmentModel, JobAttachmentViewModel } from '../models/job-attachment.model';
import { FileReturnBase64 } from '../models/file-return-base-64.model';
import { JobCleanupQueueElement } from '../models/job-cleanup-queue-element.model';
import { AssetViewModel } from '../../asset/string/models/string-view.model';
import { EditJobInformationPayload } from '../models/edit-job-information-payload';
import { ForceMonitorModelModel } from '../components/force-monitor/force-monitor.component';
import { CompletedJobSearchModel } from '../models/complete-job-search.model';
import { CompletedJobModel } from '../models/completed-job-model';
import { UserChannelViewModel } from '../../channel/models/user-channel-view.model';
import { ErrorRouteData } from '../../error/models/error-route-data.model';

// Services
import { CoreService } from '../../../shared/services';
import { UtilityService } from '../../../shared/services';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Helpers
import { UtilityHelper } from '../../../shared/helpers';

// Enums
import { StatusType } from '../../../shared/enums';
import { ChangeType } from '../enums/change-type.enum';

// Store
import { StorageActions } from '../../../shared/state/storage.action';
import { JobActions } from '../state/job.action';
import { JobState } from '../state/job.state';
import { ExpireLinkModel } from '../../../shared/models/expire-link-model';

@Injectable({
	providedIn: 'root',
})
export class JobService extends AppCleanup {
	// Endpoints
	jobUrl: string;
	channelUrl: string;
	jobCommentUrl: string;

	// Jobs Cache
	jobsCache: Job[] = [];

	// NEW: Job-specific streams
	private jobStreams = new Map<string, BehaviorSubject<JobUpdate>>();
	private jobInitialized = new Map<string, boolean>();

	jobLinks: Array<JobLink> = [];
	loadingChartData: boolean = false;

	protected newDataSentVar: BehaviorSubject<JobData[]> = new BehaviorSubject<JobData[]>(null);
	newDataSent$ = this.newDataSentVar.asObservable();

	allowCommentVisibility: boolean = true;

	jobCommentDefaultGroupsForLocalStorage?: JobCommentDefaultGroupLocalStorage[] = [];
	jobTypeIdsToShow: number[] = []; // Job type ids from subheader filter
	commentMode: boolean = false;
	addComment: boolean = false;
	jobCommentDefaultGroupId: number;
	currentJob: Job;

	commentsChanged: boolean = false;
	userChannelsChanged$: EventEmitter<boolean> = new EventEmitter(false);

	selectedUnitPeriod: UnitPeriodDto;
	protected selectedUnitPeriodChangeVar: BehaviorSubject<UnitPeriodDto> = new BehaviorSubject<UnitPeriodDto>(null);
	selectedUnitPeriodChange$ = this.selectedUnitPeriodChangeVar.asObservable();
	refreshAttachments: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // Need this in order to know when to refresh list of attachments once comment dialog is closed, and onDestroy the prompt asks for deleting the attachment.

	protected splitMergeAvailabilityRefresh$ = new Subject<number>();
	protected splitMergeRequest$ = new Subject<SplitMergeJobPayload>();

	splitMergeState$ = new BehaviorSubject<{ [key: number]: SplitMergeJobState }>({});

	lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];

	forceMonitorReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

	constructor(
		private http: HttpClient,
		private coreService: CoreService,
		private utilityService: UtilityService,
		private translateService: TranslateService,
		private store: Store,
		private spinnerService: SpinnerService,
		private router: Router
	) {
		super();

		this.jobUrl = `${this.apiUrl}api/job`;
		this.channelUrl = `${this.apiUrl}api/channel`;
		this.jobCommentUrl = `${this.apiUrl}api/jobcomment`;

		this.splitMergeAvailabilityRefresh$
			.pipe(
				filter((jobId: number) => !this.isAvailabilityRequestInProgress(jobId)),
				tap((jobId: number) => {
					const jobs = { ...this.splitMergeState$.value };
					jobs[jobId] = {
						...jobs[jobId],
						isAvailabilityRequestInProgress: true,
					};
					this.splitMergeState$.next(jobs);
				}),
				mergeMap((jobId: number) =>
					this.getJobSplitMergeAvailability(jobId).pipe(
						map((isAvailable) => {
							const jobs = { ...this.splitMergeState$.value };
							jobs[jobId] = {
								...jobs[jobId],
								isAvailabilityRequestInProgress: false,
								isAvailable,
							};
							return jobs;
						})
					)
				),
				takeUntil(this.destroy)
			)
			.subscribe(
				(x) => {
					this.splitMergeState$.next(x);
				},
				() => {
					this.splitMergeState$.next({});
				}
			);

		this.splitMergeRequest$
			.pipe(
				filter((payload) => !this.isSplitMergeRequestInProgressByPayload(payload)),
				tap((payload) => {
					const jobs = { ...this.splitMergeState$.value };

					jobs[payload.fromJobId] = {
						...jobs[payload.fromJobId],
						isSplitMergeRequestInProgress: true,
						splitMergeResult: null,
					};

					if (payload.toJobId) {
						jobs[payload.toJobId] = {
							...jobs[payload.toJobId],
							isSplitMergeRequestInProgress: true,
							splitMergeResult: null,
						};
					}

					const messageKey = payload.toJobId ? 'MODULE.SPLIT_MERGE.MERGE_STARTED' : 'MODULE.SPLIT_MERGE.SPLIT_STARTED';
					const message = `${this.translateService.instant(messageKey)}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });

					this.splitMergeState$.next(jobs);
				}),
				mergeMap((payload) => this.http.post<SplitMergeResult>(`${this.jobUrl}/${payload.fromJobId}/split-merge`, payload)),
				tap((splitMergeResult) => {
					let message: string = '';
					let type: StatusType;

					if (splitMergeResult.isSuccess) {
						message = this.translateService.instant('MODULE.SPLIT_MERGE.SUCCEEDED');
						type = StatusType.SUCCESS;
					} else {
						const messageFailed = this.translateService.instant('MODULE.SPLIT_MERGE.FAILED');
						message = `${messageFailed}: ${splitMergeResult.message}`;
						type = StatusType.DANGER;
					}

					this.coreService.toast({ message: message, type: type });
				}),
				map((splitMergeResult) => {
					const jobs = { ...this.splitMergeState$.value };

					jobs[splitMergeResult.fromJobId] = {
						...jobs[splitMergeResult.fromJobId],
						isSplitMergeRequestInProgress: false,
						splitMergeResult,
					};

					if (splitMergeResult.toJobId) {
						jobs[splitMergeResult.toJobId] = {
							...jobs[splitMergeResult.toJobId],
							isSplitMergeRequestInProgress: false,
							splitMergeResult,
						};
					}

					return jobs;
				}),
				takeUntil(this.destroy)
			)
			.subscribe(this.splitMergeState$);
	}

	getRecentJobs(): Observable<Array<Job>> {
		return this.http.get<Array<Job>>(this.jobUrl);
	}

	getActiveJobs(): Observable<Job[]> {
		return this.http.get<Job[]>(`${this.jobUrl}/active`);
	}

	getCompletedJobs(searchModel?: CompletedJobSearchModel): Observable<CompletedJobModel[]> {
		let url: string = `${this.jobUrl}/completed`;
		let params: string = '';

		if (searchModel) {
			// If set, convert start & end times to epoch
			// millis so all params can be passed via URL.
			if (searchModel.start) {
				searchModel.start = new Date(searchModel.start).getTime();
			}
			if (searchModel.end) {
				searchModel.end = new Date(searchModel.end).getTime();
			}
			params = this.utilityService.buildUrlParams(searchModel);
			url = `${url}?${params}`;
		}

		return this.http.get<CompletedJobModel[]>(url);
	}

	getNotStartedJobs(): Observable<Job[]> {
		return this.http.get<Job[]>(`${this.jobUrl}/not-started`);
	}

	getJob(key: string, forceRefresh: boolean, setToCurrent: boolean, changeReason: string = ''): Observable<Job> {
		let requestUrl = `${this.jobUrl}/${key}`;
		return this.http.get<Job>(requestUrl).pipe(tap((_) => this.prepareJob(_, forceRefresh, setToCurrent, changeReason)));
	}

	initJob(key: string, forceRefresh: boolean, setToCurrent: boolean, changeReason: string = '') {
		// If Job is not already cached, cache it
		if (forceRefresh || !this.findJob(key)) {
			this.refreshJob(key, forceRefresh, setToCurrent, changeReason);
		} else {
			let initializedJob = this.findJob(key);
			if (setToCurrent) {
				this.currentJob = initializedJob;
				this.triggerUnitPeriodChangeEvent(this.currentJob.unitPeriods[0]);
			}
			this.updateJobStream(initializedJob, ChangeType.INITIAL);
		}
	}

	initPublicJob(publicGuid: string) {
		this.getJobFromLink(publicGuid).pipe(takeUntil(this.destroy)).subscribe();
	}

	endJob(job: Job, startNewJob: boolean): Observable<Job> {
		return this.http.get<Job>(`${this.jobUrl}/${job.id}/end?endUnitPeriods=${startNewJob ? 'yes' : 'no'}`);
	}

	modifyCachedJob(jobToUpdate: Job) {
		let foundIndex = this.jobsCache.findIndex((chachedJob) => chachedJob.id === jobToUpdate.id);

		if (foundIndex >= 0) {
			this.store.dispatch(new JobActions.UpdateCachedJob(new Job(jobToUpdate)));
		} else {
			this.store.dispatch(new JobActions.AddCachedJob(new Job(jobToUpdate)));
		}

		// NEW: Use job-specific stream instead of global stream
		this.updateJobStream(this.findJob(jobToUpdate.key), ChangeType.JOB_CHANGE);
	}

	refreshJob(key: string, forceRefresh: boolean, setToCurrent: boolean, changeReason: string = '') {
		this.getJob(key, forceRefresh, setToCurrent, changeReason)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});
	}

	refreshPublicJob(key: string) {
		this.getJobFromLink(key)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});
	}

	findJob(key: string | number): Job {
		let job = this.jobsCache.find((jc) => jc.key === `${key}`);
		job = UtilityHelper.deepCopy(job);

		if (job?.start) {
			job.start = new Date(job.start);
		}

		if (job?.end) {
			job.end = new Date(job?.end);
		}

		if (job?.lastSyncRequest) {
			job.lastSyncRequest = new Date(job.lastSyncRequest);
		}

		return job;
	}

	prepareJob(job: any, forceRefresh: boolean, setToCurrent: boolean, changeReason: any = null, publicGuid: string = null) {
		if (job) {
			if (job?.message === 'Job Link has expired') {
				let errorRouteData: ErrorRouteData = {
					code: 404,
					title: 'Link expired',
					description: 'Job link has expired. Please try again later or contact the administrator',
					custom: true,
				};

				this.router.navigate(['/public-error-page'], { queryParams: errorRouteData });
				return;
			}

			let jobToCache = new Job(job);

			if (jobToCache.start) jobToCache.start = new Date(jobToCache.start);

			if (jobToCache.end) jobToCache.end = new Date(jobToCache.end);

			if (jobToCache.lastDataPoint) jobToCache.lastDataPoint.date = new Date(jobToCache.lastDataPoint.date);

			jobToCache.jobLinksActive = (jobToCache.options & JobOptions.LinksDisabled) === 0;

			let cachedJob = this.jobsCache.find((jc) => jc.id === job.id);

			if (cachedJob) {
				this.store.dispatch(new JobActions.UpdateCachedJob(new Job(jobToCache)));
			} else {
				this.store.dispatch(new JobActions.AddCachedJob(new Job(jobToCache)));
			}

			this.store.dispatch(new JobActions.UpdateActiveJob(new Job(jobToCache))); // Updates the job if it is in active jobs array

			if (setToCurrent || this.currentJob?.key === jobToCache.key) {
				this.currentJob = jobToCache;
				this.triggerUnitPeriodChangeEvent(this.currentJob.unitPeriods[0]);
			}

			// NEW: Use job-specific stream instead of global stream
			// For public jobs, use the GUID as the stream key instead of the numeric job.key
			if (publicGuid) {
				// Create a copy with the GUID as the key for the stream
				const publicJobToStream = new Job(jobToCache);
				publicJobToStream.key = publicGuid;
				this.updateJobStream(publicJobToStream, forceRefresh ? changeReason || ChangeType.JOB_CHANGE : ChangeType.INITIAL);
			} else {
				this.updateJobStream(jobToCache, forceRefresh ? changeReason || ChangeType.JOB_CHANGE : ChangeType.INITIAL);
			}
		}
	}

	getJobIdFromKey(key: string) {
		return this.http.get<number>(`${this.jobUrl}/jobkey/${key}`);
	}

	isAvailabilityRequestInProgress(jobId: number): boolean {
		return this.splitMergeState$.value[jobId]?.isAvailabilityRequestInProgress ?? false;
	}

	isSplitMergeRequestInProgress(jobId: number): boolean {
		return this.splitMergeState$.value[jobId]?.isSplitMergeRequestInProgress ?? false;
	}

	isSplitMergeRequestInProgressByPayload(payload: SplitMergeJobPayload): boolean {
		return this.isSplitMergeRequestInProgress(payload.fromJobId) || this.isSplitMergeRequestInProgress(payload.toJobId);
	}

	refreshCurrentJobSplitMergeAvailability() {
		if (this.currentJob?.id) {
			this.splitMergeAvailabilityRefresh$.next(this.currentJob.id);
		}
	}

	getJobFromLink(key: string, minutes?: number): Observable<any> {
		let requestUrl = `${this.jobUrl}/link/${key}`;

		if (minutes || minutes === 0) requestUrl += `?m=${minutes}`;

		return this.http.get<Job>(requestUrl).pipe(tap((_) => this.prepareJob(_, false, true, null, key)));
	}

	getJobData(payload: GetJobDataPayload): Observable<ApiResponse<JobDataResponse, GetDataResponseCode>> {
		this.loadingChartData = true;

		// Checking to see if payload key is a valid guid. If so, send to guid
		// endpoint path ('dataguid') . If not, send to normal endpoint path 'data'.
		let path = 'data';
		if (UtilityHelper.isGuid(payload.key)) {
			path = 'dataguid';
		}

		return this.http.post<ApiResponse<JobDataResponse, GetDataResponseCode>>(`${this.jobUrl}/${payload.key}/${path}`, payload).pipe(
			tap((_) => {
				this.loadingChartData = false;
			})
		);
	}

	generateLink(payload: GenerateJobLinkPayload, key: string): Observable<JobLink> {
		return this.http.post<JobLink>(`${this.jobUrl}/${key}/links`, payload);
	}

	getJobLinks(key: string): Observable<Array<JobLink>> {
		return this.http.get<Array<JobLink>>(`${this.jobUrl}/${key}/links`).pipe(
			tap((_) => {
				this.jobLinks = _;
			})
		);
	}
	
	expireLink(dto: ExpireLinkModel): Observable<JobLink> {
		return this.http.put<JobLink>(`${this.jobUrl}/links/expire`, dto).pipe(
			tap((expiredLink) => {
				// Update local cache.
				const index = this.jobLinks.findIndex(link => link.key === dto.key);
				if (index !== -1) this.jobLinks[index] = expiredLink;
			})
		);
	}

	editJobInformation(payload: EditJobInformationPayload): Observable<Job> {
		return this.http.post<Job>(`${this.jobUrl}/${payload.key}`, payload);
	}

	updateJobsCacheChannels(userJobChannels: UserChannelViewModel[]) {
		userJobChannels.forEach((userChannel) => {
			this.jobsCache.forEach((jobCache) => {
				jobCache.userUnitPeriodChannels.forEach((userUnitPeriodChannel) => {
					if (userUnitPeriodChannel.unitPeriodChannel.channelId === userChannel.channelId) {
						// userUnitPeriodChannel.minY = userChannel.minY;
						// userUnitPeriodChannel.maxY = userChannel.maxY;
						userUnitPeriodChannel.colorHex = userChannel.colorHex;
						userUnitPeriodChannel.lineStyle = userChannel.lineStyle;
						userUnitPeriodChannel.lineStyleOption = this.lineStyleOptions.find((lso) => lso.lineStyle === userChannel.lineStyle);
						userUnitPeriodChannel.lineWeight = userChannel.lineWeight;
						// userUnitPeriodChannel.display = userChannel.displayByDefault;
						// userUnitPeriodChannel.showGraph = userChannel.showGraph;
					}
				});
			});
		});

		this.userChannelsChanged$.emit(true);
	}

	toggleJobLinks(key: string): Observable<boolean> {
		return this.http.get<boolean>(`${this.jobUrl}/${key}/links-toggle`).pipe(
			tap((active: boolean) => {
				// Get job from store instead of jobsCache; might be stale.
				const cachedJobs = this.store.selectSnapshot(JobState.cachedJobs);
				const cachedJob = cachedJobs?.find(j => j.key === key);

				if (cachedJob) {
					// Create proper Job instance copy.
					const updatedJob = new Job(cachedJob);
					updatedJob.jobLinksActive = active;
					if (active) {
						updatedJob.options &= ~JobOptions.LinksDisabled;
					} else {
						updatedJob.options |= JobOptions.LinksDisabled;
					}

					// Update the store with the new state
					this.store.dispatch(new JobActions.UpdateCachedJob(updatedJob));
				}
			})
		);
	}

	getUnassignedJobs() {
		return this.http.get<JobBaseDto[]>(`${this.jobUrl}/unassigned`);
	}

	removeJobFromCompany(companyId: number, jobId: number) {
		const url: string = `${this.jobUrl}/removeFromCompany`;
		const data: { companyId: number; jobId: number } = {
			companyId,
			jobId,
		};

		return this.http.put<boolean>(url, data);
	}

	addPointToListener(points: JobData[]) {
		this.newDataSentVar.next(points);
	}

	applyOffsetsToDataPoints(key: string, data: JobData[]): JobData[] {
		if (!data || data.length === 0) return [];

		let jobData: JobData[] = UtilityHelper.deepCopy(data);

		let unitPeriodChannelOffsets = this.findUnitPeriodChannelOffsetsForJob(key);

		// Applying Offsets to Live Data
		jobData.forEach((d) => {
			d.c.forEach((c) => {
				let channelOffsets = unitPeriodChannelOffsets?.filter((unitPeriodChannelOffset) => unitPeriodChannelOffset.unitPeriodChannel.channelId === c.i && unitPeriodChannelOffset.activatedDateUtc <= d.d);

				let channelOffset: any = 0;

				if (channelOffsets?.length > 0) {
					channelOffset = channelOffsets[channelOffsets.length - 1].offsetValue;
				}

				c.rawValue = UtilityHelper.deepCopy(c.v);
				c.v += channelOffset;
			});
		});

		return jobData;
	}

	applyPrecisionToDataPoints(key: string, data: JobData[]): JobData[] {
		if (!data || data.length === 0) return [];

		let jobData: JobData[] = UtilityHelper.deepCopy(data);

		let unitPeriodChannels = this.findUnitPeriodChannelsForJob(key);

		// Applying Precision to Live Data
		jobData.forEach((d) => {
			d.c.forEach((c) => {
				if (c.rawValue !== null) {
					let precision = unitPeriodChannels?.find((jc) => jc.channelId === c.i)?.channel?.precision;
					c.v = +c.v.toFixed(precision);
				}
			});
		});

		return jobData;
	}

	filterOutOfRangePoints(key: string, data: JobData[]): JobData[] {
		if (!data || data.length === 0) return [];

		let jobData: JobData[] = UtilityHelper.deepCopy(data);

		let unitPeriodChannels = this.findUnitPeriodChannelsForJob(key);

		// Filtering points that are out of range
		jobData.forEach((d) => {
			d.c.forEach((c) => {
				if (unitPeriodChannels) {
					let unitPeriodChannel = unitPeriodChannels.find((dc) => dc.channelId == c.i);
					let valid =
						!!unitPeriodChannel &&
						((this.numberExists(unitPeriodChannel.channel.min) && c.v >= unitPeriodChannel.channel.min) || !this.numberExists(unitPeriodChannel.channel.min)) &&
						((this.numberExists(unitPeriodChannel.channel.max) && c.v <= unitPeriodChannel.channel.max) || !this.numberExists(unitPeriodChannel.channel.max));
					if (!valid) {
						c.v = null;
					}
				} else {
					c.v = null;
				}
			});
		});

		return jobData;
	}

	downloadJobData(key: string, unitPeriodId: number, startDate: number, endDate?: number) {
		const requestOptions: Object = {
			responseType: 'blob',
		};

		if (!endDate) {
			return this.http.get<any>(`${this.jobUrl}/sample/download/${key}?startDate=${startDate}`, requestOptions);
		} else {
			return this.http.get<any>(`${this.jobUrl}/sample/download/${key}?startDate=${startDate}&endDate=${endDate}`, requestOptions);
		}
	}

	downloadFatigueReport(key: string, unitPeriodId: number, startDate: number, endDate?: number) {
		const requestOptions: Object = {
			responseType: 'blob',
		};

		if (!endDate) {
			return this.http.get<any>(`${this.jobUrl}/sample/download/fatigue/${key}/${unitPeriodId}?startDate=${startDate}`, requestOptions);
		} else {
			return this.http.get<any>(`${this.jobUrl}/sample/download/fatigue/${key}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}`, requestOptions);
		}
	}

	/********************************** Channels **********************************/
	/**
	 * Updates a single Job Channel through API call and modifies Job Channel for Cached job. jobChange$ event will be fired after successful update.
	 * @param jobKey
	 * @param unitPeriodChannel
	 */
	updateUnitPeriodChannel(jobKey: string, unitPeriodChannel: UnitPeriodChannelDto) {
		this._putJobChannel(jobKey, unitPeriodChannel)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {
				let unitPeriodChannels = this.findUnitPeriodChannelsForJob(jobKey);

				let foundIndex = unitPeriodChannels.findIndex((ujc) => ujc.id === unitPeriodChannel.id);

				unitPeriodChannels[foundIndex] = unitPeriodChannel;

				this.updateJobStream(this.findJob(jobKey), ChangeType.UNIT_PERIO_CHANNEL);
			});
	}

	/**
	 * Updates multiple Job Channels through API call and modifies Job Channels for Cached job. jobChange$ event will be fired after successful update.
	 * @param jobKey
	 * @param jobChannels Job Channel DTO's to Update
	 */
	updateJobChannels(jobKey: string, jobChannels: UnitPeriodChannelDto[]) {
		this._putJobChannels(jobKey, jobChannels)
			.pipe(takeUntil(this.destroy))
			.subscribe((unitPeriodChannels) => {
				const message = `${this.translateService.instant('MODULE.JOB.ALERTS_UPDATED')}`;
				this.coreService.toast({ message: message, type: StatusType.SUCCESS });

				let cachedJob = this.findJob(jobKey);

				unitPeriodChannels.forEach((unitPeriodChannel) => {
					let unitPeriodChannels = this.findUnitPeriodChannelsForJob(jobKey);

					let foundIndex = unitPeriodChannels.findIndex((ujc) => ujc.id === unitPeriodChannel.id);

					unitPeriodChannels[foundIndex] = unitPeriodChannel;
				});

				this.updateJobStream(cachedJob, ChangeType.UNIT_PERIO_CHANNEL);
			});
	}

	/**
	 * Updates a single User Job Channel through API call and modifies User Job Channel for Cached job. jobChange$ event will be fired after successful update.
	 * @param key Job key
	 * @param userUnitPeriodChannel
	 */
	updateUserJobChannel(key: string, userUnitPeriodChannel: UserUnitPeriodChannelDto) {
		this._putUserJobChannel(key, userUnitPeriodChannel)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				let userUnitPeriodChannels = this.findUserUnitPeriodChannels(key, userUnitPeriodChannel.unitPeriodChannel.unitPeriodId);

				let foundIndex = userUnitPeriodChannels.findIndex((uupc) => uupc.id === userUnitPeriodChannel.id);

				userUnitPeriodChannels[foundIndex] = result;

				this.updateJobStream(this.findJob(key), ChangeType.USER_UNIT_PERIOD_CHANNEL);
			});
	}

	/**
	 * Updates multiple User Job Channel's through API call and modifies User Job Channel's for Cached job. jobChange$ event will be fired after successful update.
	 * @param jobKey
	 * @param userUnitPeriodChannels
	 */
	updateUserJobChannels(jobKey: string, userUnitPeriodChannels: UserUnitPeriodChannelDto[]) {
		return new Observable((subscriber) => {
			this._putUserJobChannels(jobKey, userUnitPeriodChannels)
				.pipe(takeUntil(this.destroy))
				.subscribe({
					next: (uupc) => {
						let userUnitPeriodChannels = this.findUserUnitPeriodChannelsForJob(jobKey);

						uupc.forEach((userJobChannel) => {
							let foundIndex = userUnitPeriodChannels.findIndex((ujc) => ujc.id == userJobChannel.id);
							userUnitPeriodChannels[foundIndex] = userJobChannel;
						});

						this.updateJobStream(this.findJob(jobKey), ChangeType.USER_UNIT_PERIOD_CHANNEL);
						subscriber.next(uupc);
						subscriber.complete();
					},
					error: (err) => {
						subscriber.error(err);
					},
				});
		});
	}

	changeJobChannelStatus(jobKey: string, unitPeriodId: number, channelId: number, status: string) {
		let unitPeriodChannels = this.findUnitPeriodChannels(jobKey, unitPeriodId);

		let findIndex = unitPeriodChannels.findIndex((jc) => jc.channelId === channelId);

		if (findIndex >= 0) {
			let jobChannel = unitPeriodChannels[findIndex];

			if (jobChannel.currentStatus === 'danger' && !jobChannel.acknowledgedAlarm) {
				jobChannel.pendingAcknowledgement = true;
			}

			if (jobChannel.currentStatus === '' && status === 'danger') unitPeriodChannels[findIndex].acknowledgedAlarm = false;

			unitPeriodChannels[findIndex].currentStatus = status;
			this.updateJobStream(this.findJob(jobKey), ChangeType.UNIT_PERIO_CHANNEL);
		}
	}

	acknowledgeChannelAlert(jobKey: string, unitPeriodId: number, channelId: number) {
		let unitPeriodChannels = this.findUnitPeriodChannels(jobKey, unitPeriodId);

		let findIndex = unitPeriodChannels.findIndex((jc) => jc.channelId === channelId);

		if (findIndex >= 0) {
			unitPeriodChannels[findIndex].acknowledgedAlarm = true;
			unitPeriodChannels[findIndex].pendingAcknowledgement = false;

			this.updateJobStream(this.findJob(jobKey), ChangeType.UNIT_PERIO_CHANNEL);
		}
	}

	addJobChannelOffset(jobKey: string, item: UnitPeriodChannelOffsetDto) {
		this._putJobChannelOffset(jobKey, item)
			.pipe(takeUntil(this.destroy))
			.subscribe((upco) => {
				let job = this.findJob(jobKey);

				let findIndex = job.unitPeriodChannelOffsets.findIndex((upc) => upc.id === upco.id);

				if (findIndex >= 0) {
					job.unitPeriodChannelOffsets[findIndex] = upco;
				} else {
					job.unitPeriodChannelOffsets.push(upco);
				}
			});
	}

	refreshJobComments(key: string) {
		this._getJobComments(key)
			.pipe(takeUntil(this.destroy))
			.subscribe((jobCommentReturn) => {
				let cachedJob = this.findJob(key);
				cachedJob.jobCommentReturn = jobCommentReturn;
				this.updateJobStream(cachedJob, ChangeType.JOB_COMMENT);
			});
	}

	refreshPublicJobComments(key: string, publicGuid: string) {
		this._getPublicJobComments(publicGuid)
			.pipe(takeUntil(this.destroy))
			.subscribe((jobCommentReturn) => {
				let cachedJob = this.findJob(key);
				cachedJob.jobCommentReturn = jobCommentReturn;
				this.updateJobStream(cachedJob, ChangeType.JOB_COMMENT);
			});
	}

	addJobComment(key: string, jobCommentDto: JobCommentDto) {
		this.spinnerService.showAppSpinner(); // Turns on app wide spinner

		this._postJobComment(key, jobCommentDto)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {
				// let cachedJob = this.findJob(key);
				// this.jobChangeVar.next(new JobChange(cachedJob, ChangeType.JOB_COMMENT));
				this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
			});
	}

	updateJobComment(key: string, jobCommentDto: JobCommentDto) {
		this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		this._putJobComment(key, jobCommentDto)
			.pipe(takeUntil(this.destroy))
			.subscribe(
				() => {
					let cachedJob = this.findJob(key);
					this.updateJobStream(cachedJob, ChangeType.JOB_COMMENT);
					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					const message = `${this.translateService.instant('GENERIC.UPDATED', { name: this.translateService.instant('MODULE.COMMENT.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				},
				(error) => {
					const message = `${this.translateService.instant('GENERIC.NOT_UPDATED', { name: this.translateService.instant('MODULE.COMMENT.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				}
			);
	}

	deleteJobComment(key: string, jobCommentId: number) {
		this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		this._deleteJobComment(key, jobCommentId)
			.pipe(takeUntil(this.destroy))
			.subscribe(
				() => {
					let cachedJob = this.findJob(key);
					let jcIndex = cachedJob.jobCommentReturn.jobComments.findIndex((jc) => jc.id === jobCommentId);

					if (jcIndex > -1) {
						cachedJob.jobCommentReturn.jobComments[jcIndex].isDeleted = true;
					}

					this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					const message = `${this.translateService.instant('GENERIC.DELETED', { name: this.translateService.instant('MODULE.COMMENT.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				},
				(error) => {
					const message = `${this.translateService.instant('GENERIC.NOT_DELETED', { name: this.translateService.instant('MODULE.COMMENT.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				}
			);
	}

	getUnitPeriodChannelOffsets(key: string) {
		this._getUnitPeriodChannelOffsets(key)
			.pipe(takeUntil(this.destroy))
			.subscribe((upco) => {
				let cachedJob = this.findJob(key);

				if (cachedJob) {
					cachedJob.unitPeriodChannelOffsets = upco;
				}
			});
	}

	getUnitPeriodChannels(key: string) {
		this._getUnitPeriodChannels(key)
			.pipe(takeUntil(this.destroy))
			.subscribe((unitPeriodChannels) => {
				let cachedJob = this.findJob(key);

				if (cachedJob) {
					cachedJob.unitPeriodChannels = unitPeriodChannels;
				}
			});
	}

	/********** Find Operations ***** */
	findUnitPeriodChannels(jobKey: string, unitPeriodId: number): UnitPeriodChannelDto[] {
		return this.jobsCache.find((jc) => jc.key === jobKey)?.unitPeriodChannels.filter((upc) => upc.unitPeriodId === unitPeriodId);
	}

	findUnitPeriodChannelsForJob(jobKey: string | number): UnitPeriodChannelDto[] {
		let job = this.jobsCache.find((jc) => jc.key === `${jobKey}`);

		if (job?.unitPeriodChannels) {
			return job.unitPeriodChannels;
		}
		return [];
	}

	findUserUnitPeriodChannels(jobKey: string, unitPeriodId: number): UserUnitPeriodChannelDto[] {
		return this.jobsCache
			.find((jc) => jc.key === jobKey)
			.userUnitPeriodChannels.filter((upc) => upc.unitPeriodChannel.unitPeriodId === unitPeriodId)
			.sort((a, b) => {
				return a.seq - b.seq;
			});
	}

	findUserUnitPeriodChannelsForJob(jobKey: string): UserUnitPeriodChannelDto[] {
		return this.jobsCache
			.find((jc) => jc.key === jobKey)
			?.userUnitPeriodChannels?.sort((a, b) => {
				return a.seq - b.seq;
			});
	}

	findUnitPeriodChannelOffsets(jobKey: string, unitPeriodId: number): UnitPeriodChannelOffsetDto[] {
		return this.jobsCache.find((jc) => jc.key === jobKey)?.unitPeriodChannelOffsets.filter((upc) => upc.unitPeriodChannel.unitPeriodId === unitPeriodId);
	}

	findUnitPeriodChannelOffsetsForJob(jobKey: string): UnitPeriodChannelOffsetDto[] {
		let job = this.jobsCache.find((jc) => jc.key === jobKey);

		if (job?.unitPeriodChannelOffsets) {
			return job.unitPeriodChannelOffsets;
		}

		return [];
	}

	/********** API Calls *************/
	/**
	 * Gets Job Channels specific to the Job for the Logged In view.
	 * @param key The key is the unique identifier for the Job. In Portal job's, this will be an ID. In Local Viewer, this will be the context key.
	 */
	_getUnitPeriodChannels(key: string) {
		return this.http.get<Array<UnitPeriodChannelDto>>(`${this.channelUrl}/job/${key}`);
	}

	/**
	 * Gets Job Channels specific to the Job for the Public view.
	 * @param key The key is the unique identifier for the Job. This will be a GUID for the Public view.
	 */
	_getPublicJobChannels(key: string) {
		return this.http.get<Array<UnitPeriodChannelDto>>(`${this.channelUrl}/job/public/${key}`);
	}

	/**
	 * Updates a single Job Channel.
	 * @param key
	 * @param jobChannelDto The Job Channel that needs to be updated.
	 */
	_putJobChannel(key: string, jobChannelDto: UnitPeriodChannelDto) {
		return this.http.put<UnitPeriodChannelDto>(`${this.channelUrl}/job/${key}`, jobChannelDto);
	}

	/**
	 * Updates multiple Job Channels.
	 * @param key
	 * @param jobChannelDtos The Job Channels that need to be updated.
	 */
	_putJobChannels(key: string, jobChannelDtos: UnitPeriodChannelDto[]) {
		return this.http.put<UnitPeriodChannelDto[]>(`${this.channelUrl}/job/${key}/channels`, jobChannelDtos);
	}

	/**
	 * Updates a single User Job Channel.
	 * @param key
	 * @param userJobChannel The User Job Channel that needs to be updated.
	 */
	_putUserJobChannel(key: string, userJobChannel: UserUnitPeriodChannelDto) {
		return this.http.put<UserUnitPeriodChannelDto>(`${this.channelUrl}/userjob/${key}`, userJobChannel);
	}

	/**
	 * Updates multiple User Job Channels.
	 * @param key
	 * @param userJobChannels The User Job Channels that need to be updated.
	 */
	_putUserJobChannels(key: string, userJobChannels: UserUnitPeriodChannelDto[]) {
		return this.http.put<UserUnitPeriodChannelDto[]>(`${this.channelUrl}/userjob/${key}/channels`, userJobChannels);
	}

	/**
	 * When the sequence of UserJobChannels are changed, this end-point will update the sequence only
	 * @param key
	 * @param userJobChannels The User Job Channels that need to be updated.
	 */
	putUserJobChannelSequences(key: string, userJobChannels: UserUnitPeriodChannelDto[]) {
		return this.http.put<UserUnitPeriodChannelDto[]>(`${this.channelUrl}/userjob/seq/${key}`, userJobChannels);
	}

	_getUnitPeriodChannelOffsets(key: string) {
		return this.http.get<UnitPeriodChannelOffsetDto[]>(`${this.channelUrl}/job/${key}/offset`);
	}

	_putJobChannelOffset(key: string, item: UnitPeriodChannelOffsetDto) {
		return this.http.post<UnitPeriodChannelOffsetDto>(`${this.channelUrl}/job/${key}/offset`, item);
	}

	_getJobComments(key: string) {
		return this.http.get<JobCommentReturnDto>(`${this.jobCommentUrl}/${key}`);
	}

	_getPublicJobComments(key: string) {
		return this.http.get<JobCommentReturnDto>(`${this.jobCommentUrl}/public/${key}`);
	}

	_postJobComment(key: string, jobComment: JobCommentDto) {
		return this.http.post<JobCommentDto>(`${this.jobCommentUrl}/${key}`, jobComment);
	}

	_putJobComment(key: string, jobComment: JobCommentDto) {
		return this.http.put<JobCommentDto>(`${this.jobCommentUrl}/${key}`, jobComment);
	}

	_deleteJobComment(key: string, jobCommentId: number) {
		return this.http.delete<JobCommentDto>(`${this.jobCommentUrl}/${key}/${jobCommentId}`);
	}

	/*Utils */
	numberExists(val: number): boolean {
		return !(val == null);
	}

	matchWithLocalStorageSettings(key: string) {
		let job = this.findJob(key);

		job.jobCommentReturn.filteredJobCommentDefaultGroups?.forEach((jobCommentDefaultGroup) => {
			let item = this.jobCommentDefaultGroupsForLocalStorage?.find((jcdfg) => jcdfg.jobId === key);

			jobCommentDefaultGroup.expanded = true;
			jobCommentDefaultGroup.visible = true;

			if (item) {
				let itemSetting = item.groupsExtended.find((ge) => ge.id === jobCommentDefaultGroup.id);

				if (itemSetting) {
					jobCommentDefaultGroup.expanded = itemSetting.expanded;
					jobCommentDefaultGroup.visible = itemSetting.visible;
				}
			}
		});
	}

	saveLocalStorageSettings() {
		this.store.dispatch(new StorageActions.SetCommentGroups(this.jobCommentDefaultGroupsForLocalStorage));
	}

	updateJobCommentDefaultGroupForLocalStorage(jobCommentDefaultGroupLocalStorage: JobCommentDefaultGroupLocalStorage) {
		let findIndex = this.jobCommentDefaultGroupsForLocalStorage.findIndex((jcdg) => jcdg.jobId === jobCommentDefaultGroupLocalStorage.jobId);

		if (findIndex >= 0) {
			this.jobCommentDefaultGroupsForLocalStorage[findIndex] = jobCommentDefaultGroupLocalStorage;
		} else {
			this.jobCommentDefaultGroupsForLocalStorage.push(jobCommentDefaultGroupLocalStorage);
		}
	}

	getFilteredJobCommentDefaultGroupsForSelection(key: string, selectedJobCommentDefaultGroupIds: number[], triggerRefresh: boolean = false) {
		let currentLocalValues = this.jobCommentDefaultGroupsForLocalStorage.find((jobCommentDefaultGroup) => jobCommentDefaultGroup.jobId === key);

		if (currentLocalValues) {
			currentLocalValues.groupsExtended.forEach((ge) => {
				if (selectedJobCommentDefaultGroupIds.includes(ge.id)) {
					ge.visible = true;
				} else {
					ge.visible = false;
				}
			});
		}

		this.matchWithLocalStorageSettings(key);

		if (triggerRefresh) this.updateJobStream(this.findJob(key), ChangeType.SETTING_CHANGE);
	}

	modifyJobSyncDate(key: string, syncDate: Date) {
		let job = this.findJob(key);

		if (job) {
			job.lastSyncRequest = syncDate;

			this.updateJobStream(job, ChangeType.SYNC_CHANGE);
		}
	}

	triggerJobChangeEvent(job: Job, changeType: ChangeType) {
		this.updateJobStream(job, changeType);
	}

	triggerUnitPeriodChangeEvent(unitPeriod: UnitPeriodDto) {
		this.selectedUnitPeriod = unitPeriod;
		this.selectedUnitPeriodChangeVar.next(unitPeriod);
	}

	sendJobDeletedEvent(key: string) {
		let job = this.findJob(key);

		if (job) {
			this.updateJobStream(job, ChangeType.JOB_DELETED);
		}
	}

	jobExists(id: number) {
		return this.http.get<boolean>(`${this.jobUrl}/exists/${id}`);
	}

	splitOrMerge(splitJobPayload: SplitMergeJobPayload) {
		this.splitMergeRequest$.next(splitJobPayload);
	}

	getJobSplitMergeAvailability(jobId: number): Observable<boolean> {
		if (this.isViewer) return of(false);

		return this.http.get<boolean>(`${this.jobUrl}/${jobId}/split-merge/availability`);
	}

	tryGetJobAsMergeTarget(toJobId: number): Observable<Job | null> {
		return this.http.get<Job | null>(`${this.jobUrl}/merge-job/${toJobId}`).pipe(
			map((jobJson) => {
				// TODO: If we don't map it to a real typescript object, name properties won't work.
				// Do we have an overall solution to this somewhere?
				if (jobJson) return new Job(jobJson);

				return null;
			})
		);
	}

	getJobsToStreamTo(unitPeriodId: number) {
		return this.http.get<Job[]>(`${this.jobUrl}/stream-list/${unitPeriodId}`);
	}

	changeUnitPeriodJob(changeUnitPeriodJob: UnitPeriodJobChangeDto) {
		return this.http.put(`${this.jobUrl}/unit-period-job`, changeUnitPeriodJob);
	}

	modifyChannelMappingForJob(channelMapping: JobTypeChannelMappingDto): Job {
		let job = this.jobsCache.find((jc) => jc.id === channelMapping.jobId);

		if (job) {
			let channelMappingIndex = job.jobTypeChannelMappings.findIndex((jtcm) => jtcm.id === channelMapping.id);

			if (channelMappingIndex >= 0) {
				job.jobTypeChannelMappings[channelMappingIndex] = channelMapping;
			} else {
				job.jobTypeChannelMappings.push(channelMapping);
			}
		}

		return job;
	}

	getJobAttachments(jobId: number): Observable<JobAttachmentViewModel[]> {
		return this.http.get<JobAttachmentViewModel[]>(`${this.jobUrl}/${jobId}/attachment`);
	}

	getPublicJobAttachments(linkGuid: string): Observable<JobAttachmentViewModel[]> {
		return this.http.get<JobAttachmentViewModel[]>(`${this.jobUrl}/link/${linkGuid}/attachment`);
	}

	addJobAttachment(jobId: number, attachment: FormData): Observable<JobAttachmentViewModel> {
		return this.http.post<JobAttachmentViewModel>(`${this.jobUrl}/${jobId}/attachment`, attachment);
	}

	downloadJobAttachment(jobId: number, attachmentId: number) {
		const requestOptions: Object = {
			responseType: 'blob',
		};

		return this.http.get<any>(`${this.jobUrl}/${jobId}/attachment/${attachmentId}`, requestOptions);
	}

	downloadPublicJobAttachment(linkGuid: string, attachmentId: number) {
		const requestOptions: Object = {
			responseType: 'blob',
		};

		return this.http.get<any>(`${this.jobUrl}/link/${linkGuid}/attachment/${attachmentId}`, requestOptions);
	}

	getJobAttachmentAsBase64(jobId: number, attachmentId: number) {
		return this.http.get<FileReturnBase64>(`${this.jobUrl}/${jobId}/attachment/base64/${attachmentId}`);
	}

	getPublicJobAttachmentAsBase64(linkGuid: string, attachmentId: number) {
		return this.http.get<FileReturnBase64>(`${this.jobUrl}/link/${linkGuid}/attachment/base64/${attachmentId}`);
	}

	editJobAttachment(jobId: number, attachmentId: number, attachment: JobAttachmentModel): Observable<JobAttachmentViewModel> {
		return this.http.put<JobAttachmentViewModel>(`${this.jobUrl}/${jobId}/attachment/${attachmentId}`, attachment);
	}

	deleteJobAttachments(jobId: number, attachmentIds: number[]): Observable<JobAttachmentViewModel[]> {
		return this.http.post<JobAttachmentViewModel[]>(`${this.jobUrl}/${jobId}/attachment/delete`, attachmentIds);
	}

	addJobAttachmentsToComment(jobId: number, commentId: number, attachmentIds: number[]) {
		return this.http.post<number[]>(`${this.jobUrl}/${jobId}/attachment/comment/${commentId}`, attachmentIds);
	}

	getCommentAttachments(jobId: number, commentId: number): Observable<JobAttachmentViewModel[]> {
		return this.http.get<JobAttachmentViewModel[]>(`${this.jobUrl}/${jobId}/comment/${commentId}/attachment`);
	}

	getForceMonitor(jobKey: string, unitPeriodId: number, startDate: number, endDate?: number) {
		if (!endDate) {
			return this.http.get<any>(`${this.jobUrl}/force-monitor/${jobKey}/${unitPeriodId}?startDate=${startDate}`);
		} else {
			return this.http.get<any>(`${this.jobUrl}/force-monitor/${jobKey}/${unitPeriodId}?startDate=${startDate}&endDate=${endDate}`);
		}
	}

	addForceMonitorModel(jobKey: string, model: ForceMonitorModelModel) {
		return this.http.post<ForceMonitorModelModel>(`${this.jobUrl}/force-monitor/${jobKey}/models`, model);
	}

	getForceMonitorModels(jobKey: string) {
		return this.http.get<ForceMonitorModelModel>(`${this.jobUrl}/force-monitor/${jobKey}/models`);
	}

	editForceMonitorModel(jobKey: string, model: ForceMonitorModelModel) {
		return this.http.put<ForceMonitorModelModel>(`${this.jobUrl}/force-monitor/${jobKey}/models`, model);
	}

	deleteForceMonitorModel(jobKey: string, modelId: number) {
		return this.http.delete<ForceMonitorModelModel>(`${this.jobUrl}/force-monitor/${jobKey}/models/${modelId}`);
	}

	getJobsQueuedForDeletion() {
		return this.http.get<JobCleanupQueueElement[]>(`${this.jobUrl}/queuedForDeletion`);
	}

	getJobBops(jobKey: string | number) {
		return this.http.get<AssetViewModel[]>(`${this.jobUrl}/${jobKey}/bop`);
	}

	/********************************** NEW: Job-Specific Streams **********************************/

	/**
	 * Get or create job-specific observable stream
	 * @param jobKey The job key to get the stream for
	 * @returns Observable<JobUpdate> that emits updates for the specific job
	 */
	getJobStream(jobKey: string): Observable<JobUpdate> {
		if (!this.jobStreams.has(jobKey)) {
			this.jobStreams.set(jobKey, new BehaviorSubject<JobUpdate>(null));
			this.jobInitialized.set(jobKey, false);
		}
		return this.jobStreams
			.get(jobKey)
			.asObservable()
			.pipe(filter((update) => update !== null));
	}

	/**
	 * Update specific job stream with job data and change type
	 * @param job The job data
	 * @param changeType The type of change that occurred
	 */
	private updateJobStream(job: Job, changeType: ChangeType) {
		const jobKey = job.key;

		if (!this.jobStreams.has(jobKey)) {
			this.jobStreams.set(jobKey, new BehaviorSubject<JobUpdate>(null));
		}

		// Update the job cache
		this.updateJobInCache(job);

		// Create job update with timestamp
		const jobUpdate: JobUpdate = {
			job: job,
			changeType: changeType,
			timestamp: new Date(),
		};

		// Emit to job-specific stream
		this.jobStreams.get(jobKey).next(jobUpdate);

		// Mark as initialized if it's initial load
		if (changeType === ChangeType.INITIAL) {
			this.jobInitialized.set(jobKey, true);
		}
	}

	/**
	 * Update job cache without emitting to streams
	 * @param job The job to update in cache
	 */
	private updateJobInCache(job: Job) {
		let foundIndex = this.jobsCache.findIndex((cachedJob) => cachedJob.id === job.id);

		if (foundIndex >= 0) {
			this.store.dispatch(new JobActions.UpdateCachedJob(new Job(job)));
		} else {
			this.store.dispatch(new JobActions.AddCachedJob(new Job(job)));
		}

		this.store.dispatch(new JobActions.UpdateActiveJob(new Job(job))); // Updates the job if it is in active jobs array
	}

	/**
	 * Get job initial load observable (filters for INITIAL change type)
	 * @param jobKey The job key
	 * @returns Observable that emits once when job is initially loaded
	 */
	getJobInitialLoad(jobKey: string): Observable<Job> {
		return this.getJobStream(jobKey).pipe(
			filter((update) => update.changeType === ChangeType.INITIAL),
			map((update) => update.job),
			take(1)
		);
	}

	/**
	 * Get job comment updates observable (filters for JOB_COMMENT change type)
	 * @param jobKey The job key
	 * @returns Observable that emits when job comments change
	 */
	getJobCommentUpdates(jobKey: string): Observable<Job> {
		return this.getJobStream(jobKey).pipe(
			filter((update) => update.changeType === ChangeType.JOB_COMMENT),
			map((update) => update.job)
		);
	}

	/**
	 * Get job channel updates observable (filters for channel-related change types)
	 * @param jobKey The job key
	 * @returns Observable that emits when job channels change
	 */
	getJobChannelUpdates(jobKey: string): Observable<Job> {
		return this.getJobStream(jobKey).pipe(
			filter((update) => update.changeType === ChangeType.UNIT_PERIO_CHANNEL || update.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL || update.changeType === ChangeType.UNIT_PERIO_CHANNEL_OFFSET),
			map((update) => update.job)
		);
	}

	/**
	 * Update job from SignalR data
	 * @param jobData The job data received from SignalR
	 * @param changeType The type of change
	 */
	updateJobFromSignalR(jobData: Job, changeType: ChangeType) {
		const cachedJob = this.findJob(jobData.key);
		if (cachedJob) {
			// Merge SignalR data with cached job (preserve local state)
			const updatedJob = this.mergeJobData(cachedJob, jobData);
			this.updateJobStream(updatedJob, changeType);
		}
	}

	/**
	 * Merge SignalR job data with cached job data
	 * @param cachedJob The existing cached job
	 * @param signalRJob The job data from SignalR
	 * @returns Merged job data
	 */
	private mergeJobData(cachedJob: Job, signalRJob: Job): Job {
		// For now, simple merge - can be enhanced based on specific needs
		const merged = UtilityHelper.deepCopy(cachedJob);

		// Update key fields from SignalR
		if (signalRJob.status) merged.status = signalRJob.status;
		if (signalRJob.lastDataPoint) merged.lastDataPoint = signalRJob.lastDataPoint;
		if (signalRJob.end) merged.end = new Date(signalRJob.end);

		return merged;
	}

	/**
	 * Clean up job stream when no longer needed
	 * @param jobKey The job key to cleanup
	 */
	cleanupJobStream(jobKey: string) {
		if (this.jobStreams.has(jobKey)) {
			this.jobStreams.get(jobKey).complete();
			this.jobStreams.delete(jobKey);
			this.jobInitialized.delete(jobKey);
		}
	}

	/**
	 * Get statistics about current job streams (for debugging)
	 * @returns Object with job stream statistics
	 */
	getJobStreamStats(): { [jobKey: string]: { initialized: boolean; subscriberCount: number } } {
		const stats = {};
		for (const [jobKey, stream] of this.jobStreams) {
			stats[jobKey] = {
				initialized: this.jobInitialized.get(jobKey),
				subscriberCount: stream.observers?.length || 0,
			};
		}
		return stats;
	}
}
